import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { formatPaise, formatError } from '@/lib/api';

const dateText = (iso) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/** How many ads the customer can make: the free first ad, their current plan, and plans to buy
 * from the wallet. onBought runs after a plan is paid for, so the page can show the new balance. */
export default function AdPlanCard({ onBought, onNeedMoney }) {
  const [status, setStatus] = useState(null);
  const [buying, setBuying] = useState('');

  const load = () => api.get('/api/wallet/plan').then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buy = async (plan) => {
    if (!window.confirm(`Pay ${formatPaise(plan.price_paise)} from your wallet for the ${plan.name}?`)) return;
    setBuying(plan.id);
    try {
      const { data } = await api.post('/api/wallet/plan', { plan: plan.id });
      setStatus(data);
      toast.success(`${plan.name} started: ${plan.ads} ads for ${plan.days} days`);
      onBought?.(data.balance_paise);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      if (err.response?.status === 402) onNeedMoney?.();
    }
    setBuying('');
  };

  if (!status) return null;
  const { plan } = status;
  return (
    <section className="neo-card p-6" aria-labelledby="plan-title" data-testid="ad-plan-card">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-butter border-2 border-ink flex items-center justify-center">
          <Sparkles className="w-5 h-5" aria-hidden="true" />
        </span>
        <h2 id="plan-title" className="text-lg font-extrabold font-heading">Your ads plan</h2>
      </div>
      <p className="text-sm mb-4" data-testid="plan-summary">
        {status.free_ad_left && 'Your first ad is free. '}
        {plan
          ? <><strong>{plan.ads_left} of {plan.ads_total} ads left</strong> on your {plan.name}, until {dateText(plan.ends_at)}.</>
          : !status.free_ad_left && 'You have no ads left. Pick a plan to make more.'}
      </p>
      {!plan && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {status.plans.filter(p => p.available).map(p => (
            <div key={p.id} className="rounded-xl border-2 border-ink p-4 flex flex-col gap-2" data-testid={`plan-${p.id}`}>
              <p className="font-bold">{p.name}</p>
              <p className="text-2xl font-extrabold font-heading">{formatPaise(p.price_paise)}</p>
              <p className="text-xs text-muted-foreground">{p.ads} ads within {p.days} days</p>
              <Button className="gradient-bg mt-1" disabled={!!buying} onClick={() => buy(p)} data-testid={`buy-plan-${p.id}`}>
                {buying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy from wallet'}
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
        An ad counts when our AI makes it. Unused ads end with the plan. Ad spend is separate: it&apos;s held from your wallet when you launch, with our 15% fee and GST.
      </p>
    </section>
  );
}
